import { useCallback, useEffect } from 'react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import {
  ExternalTokenAuthMode,
  useExternalTokenAuth,
} from '../resources/agent-documents';
import { useGetOrganization } from '../resources/organization';
import { useAgentSessionStore } from '../stores/agent-session-store';

const ONE_SECOND_MS = 1000;
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * ONE_SECOND_MS;
const MIN_REFRESH_DELAY_MS = ONE_SECOND_MS;
const AGENT_TOKEN_AUTH_RETRY_COUNT = 2;

const getTokenExpiryMs = (token: string): number | null => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);

    return exp ? exp * ONE_SECOND_MS : null;
  } catch {
    return null;
  }
};

const getMsUntilRefresh = (token: string): number | null => {
  const expiresAtMs = getTokenExpiryMs(token);
  if (expiresAtMs === null) return null;

  return expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS;
};

interface UseExternalTokenOptions {
  /**
   * Overrides the default `aiEnabled`-based gate that decides whether to fetch
   * the token. Use this when a feature has its own enablement flag (e.g. the
   * internal-portal bill-pay agent gated by `billPayEnabled`).
   */
  enabled?: boolean;
  /**
   * How the Bearer token sent to the agent is obtained. Defaults to `franz`
   * (spendbrain). The internal-portal bill-pay agent uses `entra-agent`.
   */
  authMode?: ExternalTokenAuthMode;
}

export const useExternalToken = (
  organizationId?: string,
  options?: UseExternalTokenOptions
) => {
  const { data: organizationData } = useGetOrganization(organizationId);
  const { agentToken, refreshNonce, setAgentToken, setStatus } =
    useAgentSessionStore();

  const isEnabled = options?.enabled ?? organizationData?.aiEnabled ?? false;

  // The `entra-agent` (internal-portal bill-pay) agent is served from a fixed
  // host with no per-org port; only the spendbrain (`franz`) path uses `aiPort`.
  const aiPort =
    options?.authMode === 'entra-agent' ? null : organizationData?.aiPort;

  const { mutate: getAgentToken } = useExternalTokenAuth(
    organizationId ?? '',
    aiPort,
    {
      retry: AGENT_TOKEN_AUTH_RETRY_COUNT,
      onMutate: () => setStatus('loading'),
      onSuccess: data => {
        const token = data?.access_token;
        if (token && typeof token === 'string') {
          setAgentToken(token);
          setStatus('ready');
        } else setStatus('error');
      },
      onError: () => setStatus('error'),
    },
    { authMode: options?.authMode }
  );

  useEffect(() => {
    if (organizationId && isEnabled) {
      setAgentToken(null);
      getAgentToken({ orgId: organizationId });
    }
  }, [organizationId, isEnabled, getAgentToken, setAgentToken]);

  const refreshToken = useCallback(() => {
    if (organizationId) getAgentToken({ orgId: organizationId });
  }, [organizationId, getAgentToken]);

  useEffect(() => {
    if (!agentToken || !organizationId) return;

    const msUntilRefresh = getMsUntilRefresh(agentToken);
    if (msUntilRefresh === null) return;

    const delay = Math.max(msUntilRefresh, MIN_REFRESH_DELAY_MS);
    const timeoutId = setTimeout(refreshToken, delay);

    return () => clearTimeout(timeoutId);
  }, [refreshNonce, agentToken, organizationId, refreshToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== 'visible' ||
        !organizationId ||
        !isEnabled ||
        !agentToken
      )
        return;

      const msUntilRefresh = getMsUntilRefresh(agentToken);
      if (msUntilRefresh !== null && msUntilRefresh < 0) refreshToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [agentToken, organizationId, isEnabled, refreshToken]);

  return {
    agentToken,
    isAiEnabled: organizationData?.aiEnabled ?? false,
    organizationData,
  };
};
